import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "group_last_read";

function getLastRead(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch { return {}; }
}

function setLastRead(groupId: string) {
  const data = getLastRead();
  data[groupId] = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function useUnreadMessages() {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  // Ref, not state: only read inside the realtime handler below. Keeping
  // it as state caused an effect-loop — setMyGroupIds created a fresh
  // Set reference on every fetch, re-firing the subscribing effect.
  const myGroupIdsRef = useRef<Set<string>>(new Set());

  const fetchUnread = useCallback(async () => {
    if (!user) { setUnreadCount(0); myGroupIdsRef.current = new Set(); return; }

    const [{ data: memberGroups }, { data: ownedGroups }] = await Promise.all([
      supabase.from("bracket_group_members").select("group_id").eq("user_id", user.id),
      supabase.from("bracket_groups").select("id").eq("owner_id", user.id),
    ]);

    const ids = new Set<string>();
    memberGroups?.forEach((g) => ids.add(g.group_id));
    ownedGroups?.forEach((g) => ids.add(g.id));
    myGroupIdsRef.current = ids;
    if (ids.size === 0) { setUnreadCount(0); return; }

    const lastRead = getLastRead();
    const sinceByGroup: Record<string, string> = {};
    ids.forEach((gid) => { sinceByGroup[gid] = lastRead[gid] || "1970-01-01T00:00:00Z"; });

    // Batched: ONE RPC call, server does the COUNT per group.
    // Was N round-trips before — one per group.
    const { data, error } = await supabase.rpc("group_unread_counts", {
      p_since_by_group: sinceByGroup,
    });
    if (error) { console.warn("[unread] rpc failed:", error); return; }

    const total = ((data as any[]) || []).reduce((sum, r) => sum + Number(r.unread || 0), 0);
    setUnreadCount(total);
  }, [user]);

  useEffect(() => {
    fetchUnread();

    if (!user) return;

    const channel = supabase
      .channel("unread-counter")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "group_messages" },
        (payload) => {
          const msg = payload.new as { user_id: string; group_id: string };
          // Only bump the badge for messages in groups this user actually
          // belongs to — the realtime payload isn't RLS-filtered, so
          // without this check any message platform-wide would inflate it.
          if (msg.user_id !== user.id && myGroupIdsRef.current.has(msg.group_id)) {
            setUnreadCount((prev) => prev + 1);
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchUnread]);

  const markGroupRead = useCallback((groupId: string) => {
    setLastRead(groupId);
    fetchUnread();
  }, [fetchUnread]);

  return { unreadCount, markGroupRead, refreshUnread: fetchUnread };
}
